import QuickBooksSourceBanner from './QuickBooksSourceBanner';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
} from '@mui/material';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import quickbooksService, {
  formatCurrencyPrecise,
  formatDate,
  getEntityColor,
} from '../../services/quickbooksService';
import { PageHeader } from '../ds';

export default function ReconciliationView() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [entities, setEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState('all');
  const [reconciliationSummary, setReconciliationSummary] = useState(null);
  const [outstandingBills, setOutstandingBills] = useState([]);
  const [outstandingInvoices, setOutstandingInvoices] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOverdue, setFilterOverdue] = useState(false);

  useEffect(() => {
    loadData();
  }, [selectedEntity]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = selectedEntity !== 'all' ? { connector_id: selectedEntity } : {};

      const [entitiesRes, summaryRes, billsRes, invoicesRes] = await Promise.all([
        quickbooksService.getQuickBooksEntities(),
        quickbooksService.getReconciliationSummary(params),
        quickbooksService.getBillPaymentMismatches(params),
        quickbooksService.getInvoicePaymentMismatches(params),
      ]);

      if (entitiesRes.success) setEntities(entitiesRes.data);
      if (summaryRes.success) setReconciliationSummary(summaryRes.data);
      if (billsRes.success) setOutstandingBills(billsRes.data || []);
      if (invoicesRes.success) setOutstandingInvoices(invoicesRes.data || []);

    } catch (err) {
      console.error('Error loading reconciliation data:', err);
      setError('Failed to load reconciliation data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEntityChange = (event) => {
    setSelectedEntity(event.target.value);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'unpaid': return 'error';
      case 'partial': return 'warning';
      case 'paid': return 'success';
      default: return 'default';
    }
  };

  const getPaymentStatusLabel = (status) => {
    switch (status) {
      case 'unpaid': return 'Unpaid';
      case 'partial': return 'Partial';
      case 'paid': return 'Paid';
      default: return status || 'Unknown';
    }
  };

  const getOverdueColor = (daysOverdue) => {
    if (daysOverdue > 90) return 'error.main';
    if (daysOverdue > 60) return 'error.light';
    if (daysOverdue > 30) return 'warning.main';
    if (daysOverdue > 0) return 'warning.light';
    return 'success.main';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const totalOutstanding = (outstandingBills?.length || 0) + (outstandingInvoices?.length || 0);
  const totalBillsValue = outstandingBills.reduce((sum, b) => sum + (b.balance || 0), 0);
  const totalInvoicesValue = outstandingInvoices.reduce((sum, i) => sum + (i.balance || 0), 0);
  const overdueCount = [...outstandingBills, ...outstandingInvoices].filter(item => item.days_overdue > 0).length;

  return (
    <Box sx={{ p: 3 }}>
      <QuickBooksSourceBanner />
      {/* Header */}
      <PageHeader
        primary="Outstanding Items"
        metadata={[{ label: 'Bills and invoices requiring attention across entities' }]}
      />
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3, justifyContent: 'flex-end' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Entity</InputLabel>
            <Select
              value={selectedEntity}
              label="Entity"
              onChange={handleEntityChange}
            >
              <MenuItem value="all">All Entities</MenuItem>
              {entities.map((entity) => (
                <MenuItem key={entity.connector_id} value={entity.connector_id}>
                  {entity.company_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Refresh Data (r)">
            <IconButton onClick={loadData} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
            }}
            onClick={() => { setFilterOverdue(false); setActiveTab(0); }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Total Outstanding
                  </Typography>
                  <Typography
                    variant="h4"
                    component="div"
                    sx={{
                      fontWeight: 600,
                      color: totalOutstanding > 0 ? 'warning.main' : 'success.main'
                    }}
                  >
                    {totalOutstanding}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    items need attention
                  </Typography>
                </Box>
                <Box sx={{
                  backgroundColor: totalOutstanding > 0 ? 'warning.light' : 'success.light',
                  borderRadius: '50%',
                  p: 1.5,
                }}>
                  {totalOutstanding > 0 ? (
                    <WarningAmberIcon sx={{ color: 'warning.main' }} />
                  ) : (
                    <CheckCircleIcon sx={{ color: 'success.main' }} />
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              cursor: overdueCount > 0 ? 'pointer' : 'default',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': overdueCount > 0 ? { transform: 'translateY(-2px)', boxShadow: 4 } : {},
              border: filterOverdue ? '2px solid' : 'none',
              borderColor: filterOverdue ? 'error.main' : 'transparent',
            }}
            onClick={() => { if (overdueCount > 0) { setFilterOverdue(true); setActiveTab(0); } }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Overdue Items
                  </Typography>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 600, color: overdueCount > 0 ? 'error.main' : 'success.main' }}>
                    {overdueCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    past due date
                  </Typography>
                </Box>
                <Box sx={{
                  backgroundColor: overdueCount > 0 ? 'error.light' : 'success.light',
                  borderRadius: '50%',
                  p: 1.5,
                }}>
                  <ErrorIcon sx={{ color: overdueCount > 0 ? 'error.main' : 'success.main' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
              border: activeTab === 0 && !filterOverdue ? '2px solid' : 'none',
              borderColor: activeTab === 0 && !filterOverdue ? 'info.main' : 'transparent',
            }}
            onClick={() => { setFilterOverdue(false); setActiveTab(0); }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Outstanding Bills
                  </Typography>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 600, color: 'info.main' }}>
                    {outstandingBills.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {formatCurrencyPrecise(totalBillsValue)}
                  </Typography>
                </Box>
                <Box sx={{
                  backgroundColor: 'info.light',
                  borderRadius: '50%',
                  p: 1.5,
                }}>
                  <SyncAltIcon sx={{ color: 'info.main' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
              border: activeTab === 1 ? '2px solid' : 'none',
              borderColor: activeTab === 1 ? 'secondary.main' : 'transparent',
            }}
            onClick={() => { setFilterOverdue(false); setActiveTab(1); }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Outstanding Invoices
                  </Typography>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 600, color: 'secondary.main' }}>
                    {outstandingInvoices.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {formatCurrencyPrecise(totalInvoicesValue)}
                  </Typography>
                </Box>
                <Box sx={{
                  backgroundColor: 'secondary.light',
                  borderRadius: '50%',
                  p: 1.5,
                }}>
                  <SyncAltIcon sx={{ color: 'secondary.main' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Key Insight Alert */}
      {overdueCount > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Overdue Items:</strong> {overdueCount} bills or invoices are past their due date and require attention.
          </Typography>
        </Alert>
      )}

      {totalOutstanding === 0 && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>All Clear:</strong> No outstanding bills or invoices found.
          </Typography>
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Outstanding Bills
                {outstandingBills.length > 0 && (
                  <Chip label={outstandingBills.length} size="small" color="warning" />
                )}
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Outstanding Invoices
                {outstandingInvoices.length > 0 && (
                  <Chip label={outstandingInvoices.length} size="small" color="warning" />
                )}
              </Box>
            }
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                Outstanding Bills
              </Typography>
              <TextField
                size="small"
                placeholder="Search vendor or bill #..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 300 }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Bills with outstanding balances that need to be paid or reconciled.
              {filterOverdue && (
                <Chip
                  label="Showing overdue only"
                  size="small"
                  color="error"
                  onDelete={() => setFilterOverdue(false)}
                  sx={{ ml: 2 }}
                />
              )}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Bill #</TableCell>
                    <TableCell>Vendor</TableCell>
                    <TableCell>Entity</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell align="right">Total Amount</TableCell>
                    <TableCell align="right">Balance</TableCell>
                    <TableCell align="right">Days Overdue</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {outstandingBills
                    .filter(bill => !filterOverdue || bill.days_overdue > 0)
                    .filter(bill =>
                      bill.vendor_ref_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      bill.doc_number?.toLowerCase().includes(searchTerm.toLowerCase())
                    )
                    .map((bill, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography fontWeight={500}>
                            {bill.doc_number || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>{bill.vendor_ref_name || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={bill.entity_name}
                            size="small"
                            sx={{
                              backgroundColor: getEntityColor(bill.entity_name),
                              color: 'white',
                            }}
                          />
                        </TableCell>
                        <TableCell>{formatDate(bill.txn_date)}</TableCell>
                        <TableCell>{formatDate(bill.due_date)}</TableCell>
                        <TableCell align="right">
                          {formatCurrencyPrecise(bill.total_amount)}
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={600} color="error.main">
                            {formatCurrencyPrecise(bill.balance)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            fontWeight={600}
                            sx={{ color: getOverdueColor(bill.days_overdue) }}
                          >
                            {bill.days_overdue > 0 ? `${bill.days_overdue} days` : 'Current'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getPaymentStatusLabel(bill.payment_status)}
                            size="small"
                            color={getPaymentStatusColor(bill.payment_status)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  {outstandingBills.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Box sx={{ py: 3 }}>
                          <CheckCircleIcon color="success" sx={{ fontSize: 48, mb: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            No outstanding bills found
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {outstandingBills.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'right' }}>
                Showing {outstandingBills
                  .filter(b => !filterOverdue || b.days_overdue > 0)
                  .filter(b => b.vendor_ref_name?.toLowerCase().includes(searchTerm.toLowerCase()) || b.doc_number?.toLowerCase().includes(searchTerm.toLowerCase())).length} of {outstandingBills.length} bills
                {filterOverdue && ` (${outstandingBills.filter(b => b.days_overdue > 0).length} overdue)`}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                Outstanding Invoices
              </Typography>
              <TextField
                size="small"
                placeholder="Search customer or invoice #..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 300 }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Invoices with outstanding balances that need to be collected or reconciled.
              {filterOverdue && (
                <Chip
                  label="Showing overdue only"
                  size="small"
                  color="error"
                  onDelete={() => setFilterOverdue(false)}
                  sx={{ ml: 2 }}
                />
              )}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice #</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Entity</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell align="right">Total Amount</TableCell>
                    <TableCell align="right">Balance</TableCell>
                    <TableCell align="right">Days Overdue</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {outstandingInvoices
                    .filter(inv => !filterOverdue || inv.days_overdue > 0)
                    .filter(inv =>
                      inv.customer_ref_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      inv.doc_number?.toLowerCase().includes(searchTerm.toLowerCase())
                    )
                    .map((invoice, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography fontWeight={500}>
                            {invoice.doc_number || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>{invoice.customer_ref_name || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={invoice.entity_name}
                            size="small"
                            sx={{
                              backgroundColor: getEntityColor(invoice.entity_name),
                              color: 'white',
                            }}
                          />
                        </TableCell>
                        <TableCell>{formatDate(invoice.txn_date)}</TableCell>
                        <TableCell>{formatDate(invoice.due_date)}</TableCell>
                        <TableCell align="right">
                          {formatCurrencyPrecise(invoice.total_amount)}
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={600} color="success.main">
                            {formatCurrencyPrecise(invoice.balance)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            fontWeight={600}
                            sx={{ color: getOverdueColor(invoice.days_overdue) }}
                          >
                            {invoice.days_overdue > 0 ? `${invoice.days_overdue} days` : 'Current'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getPaymentStatusLabel(invoice.payment_status)}
                            size="small"
                            color={getPaymentStatusColor(invoice.payment_status)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  {outstandingInvoices.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Box sx={{ py: 3 }}>
                          <CheckCircleIcon color="success" sx={{ fontSize: 48, mb: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            No outstanding invoices found
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {outstandingInvoices.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'right' }}>
                Showing {outstandingInvoices
                  .filter(i => !filterOverdue || i.days_overdue > 0)
                  .filter(i => i.customer_ref_name?.toLowerCase().includes(searchTerm.toLowerCase()) || i.doc_number?.toLowerCase().includes(searchTerm.toLowerCase())).length} of {outstandingInvoices.length} invoices
                {filterOverdue && ` (${outstandingInvoices.filter(i => i.days_overdue > 0).length} overdue)`}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
